import type { CSSProperties } from 'react'
import type { CalendarOccurrence } from '@zendia/frontend-shared'

/**
 * Where a timed occurrence sits in a day column, in pixels from midnight. Each hour is 64px, and
 * an occurrence is never drawn shorter than 20px so its title stays readable.
 */
export const getTimedEventBox = (
  occurrence: CalendarOccurrence,
): { top: number; height: number } => {
  const startTime =
    occurrence.startTime instanceof Date ? occurrence.startTime : new Date(occurrence.startTime)
  const endTime =
    occurrence.endTime instanceof Date ? occurrence.endTime : new Date(occurrence.endTime)
  const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)
  return {
    top: startTime.getHours() * 64 + (startTime.getMinutes() / 60) * 64,
    height: Math.max(durationHours * 64, 20),
  }
}

/** The column an occurrence takes within a group of occurrences that overlap in time. */
export interface OverlapLayout {
  column: number
  columns: number
}

/**
 * Lays out occurrences that overlap side by side, so none hides another. Occurrences that overlap
 * directly or through a chain form a cluster; each one takes the leftmost column free when it
 * starts, and every occurrence in a cluster is divided by the cluster's column count so widths
 * line up. Works on the drawn boxes, not raw times, so the 20px minimum height counts as overlap.
 *
 * @returns one layout per box, in the same order as `boxes`
 */
export const layoutOverlappingEvents = (
  boxes: { top: number; height: number }[],
): OverlapLayout[] => {
  const layouts: OverlapLayout[] = new Array(boxes.length)
  // Earliest first; of those starting together, the longest takes the leftmost column
  const order = boxes
    .map((_, i) => i)
    .sort((a, b) => boxes[a].top - boxes[b].top || boxes[b].height - boxes[a].height)

  let cluster: number[] = []
  let columnBottoms: number[] = []
  let clusterBottom = -Infinity

  const closeCluster = () => {
    for (const i of cluster) layouts[i].columns = columnBottoms.length
    cluster = []
    columnBottoms = []
    clusterBottom = -Infinity
  }

  for (const i of order) {
    const top = boxes[i].top
    const bottom = top + boxes[i].height
    if (top >= clusterBottom) closeCluster()

    let column = columnBottoms.findIndex((columnBottom) => columnBottom <= top)
    if (column === -1) column = columnBottoms.length
    columnBottoms[column] = bottom

    layouts[i] = { column, columns: 0 }
    cluster.push(i)
    clusterBottom = Math.max(clusterBottom, bottom)
  }
  closeCluster()

  return layouts
}

/** Horizontal position for an occurrence's card: a 4px inset each side and a 2px gap between columns. */
export const overlapLayoutStyle = ({ column, columns }: OverlapLayout): CSSProperties => ({
  left: `calc(4px + (100% - 8px) * ${column} / ${columns})`,
  width: `calc((100% - 8px) / ${columns} - ${columns > 1 ? 2 : 0}px)`,
})

/**
 * Get contrast color (black or white) based on background color
 */
// Events on calendars without a color of their own (the user's own calendars) use this.
export const DEFAULT_EVENT_COLOR = '#3b82f6'

/**
 * The color to draw an occurrence in: its calendar's color when it has one -- a calendar synced
 * from Google carries that calendar's Google color -- otherwise the default.
 */
export const getOccurrenceColor = (
  occurrence: { calendarId: string },
  calendarColors: Record<string, string>,
): string => calendarColors[occurrence.calendarId] ?? DEFAULT_EVENT_COLOR

export const getContrastColor = (backgroundColor: string): string => {
  // Remove # if present
  const hex = backgroundColor.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF'
}

/**
 * Format date to display format
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format date and time together (e.g., "Mon, May 5 at 9:00 AM")
 */
export const formatDateTime = (date: Date): string => {
  const datePart = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${datePart} at ${timePart}`
}

/**
 * Format time to display format
 */
export const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

/**
 * Format time to short format (e.g., "9am")
 */
export const formatTimeShort = (date: Date): string => {
  return date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(' ', '')
    .toLowerCase()
}

/**
 * Get hours array (0-23)
 */
export const getHours = (): number[] => {
  return Array.from({ length: 24 }, (_, i) => i)
}

/**
 * Get days in month for calendar grid
 */
export const getDaysInMonth = (date: Date): Date[] => {
  const year = date.getFullYear()
  const month = date.getMonth()
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const days: Date[] = []

  // Add days from previous month to fill the grid
  const startDay = firstDay.getDay()
  for (let i = startDay - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i))
  }

  // Add all days of current month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    days.push(new Date(year, month, day))
  }

  // Add days from next month to fill the grid
  const remainingDays = 42 - days.length // 6 rows × 7 days
  for (let day = 1; day <= remainingDays; day++) {
    days.push(new Date(year, month + 1, day))
  }

  return days
}

/**
 * Check if an event occurs on a specific day (in local time)
 */
export const isEventInDay = (occurrence: CalendarOccurrence, day: Date): boolean => {
  // For all-day events, use localStart which is in the event's timezone
  if (occurrence.isAllDay === true && occurrence.localStart) {
    const eventDate = new Date(occurrence.localStart)
    const dayDate = new Date(day.getFullYear(), day.getMonth(), day.getDate())
    const result =
      eventDate.getFullYear() === dayDate.getFullYear() &&
      eventDate.getMonth() === dayDate.getMonth() &&
      eventDate.getDate() === dayDate.getDate()
    return result
  }

  // For timed events, use startTime/endTime
  const eventStart =
    occurrence.startTime instanceof Date ? occurrence.startTime : new Date(occurrence.startTime)
  const eventEnd =
    occurrence.endTime instanceof Date ? occurrence.endTime : new Date(occurrence.endTime)
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate())
  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59)

  const result = eventStart <= dayEnd && eventEnd >= dayStart
  return result
}

/**
 * Format date to YYYY-MM-DD
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parse a YYYY-MM-DD search param into a local date. Returns undefined for anything malformed or
 * not a real calendar day (e.g. 2026-02-30), so callers can fall back to today.
 */
export const parseDateParam = (value: unknown): Date | undefined => {
  if (typeof value !== 'string') return undefined
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return undefined
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined
  }
  return date
}

/** Validated `?date=` style search param: the value if it parses as a calendar day, else undefined. */
export const validDateParam = (value: unknown): string | undefined =>
  parseDateParam(value) ? (value as string) : undefined

/**
 * Search param describing when a newly added event starts, for the confirmation shown after
 * returning from the event form: YYYY-MM-DD for an all-day event, otherwise its local start
 * (YYYY-MM-DDTHH:mm:ss, as typed, in the event's own time zone).
 */
export const toEventStartParam = (localStart: string, isAllDay: boolean): string =>
  isAllDay ? localStart.slice(0, 10) : localStart

/**
 * Human-readable form of a `toEventStartParam` value, e.g. "Thu, Oct 22 at 3:00 PM" or
 * "Thu, Oct 22 (all day)". Returns undefined for anything malformed.
 */
export const describeEventStart = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined

  const allDay = parseDateParam(value)
  if (allDay) {
    const datePart = allDay.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
    return `${datePart} (all day)`
  }

  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(value)
  const start = match ? parseDateParam(match[1]) : undefined
  if (!match || !start) return undefined
  const [hours, minutes] = [Number(match[2]), Number(match[3])]
  if (hours > 23 || minutes > 59) return undefined
  start.setHours(hours, minutes)
  return formatDateTime(start)
}

/**
 * Format date to local datetime string YYYY-MM-DDTHH:mm:ss (without timezone)
 */
export const formatLocalDateTime = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

/** JS `Date#getDay()` index (0 = Sunday) of each `weekStart` setting. */
const WEEK_START_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

/**
 * First day of the week containing `date`, as a local midnight. `weekStart` is the user's setting;
 * a null/undefined value (or an unknown one) means the week starts on Sunday.
 */
export const getWeekStartDate = (date: Date, weekStart?: string | null): Date => {
  const startIndex = WEEK_START_INDEX[weekStart ?? 'sunday'] ?? 0
  const offset = (date.getDay() - startIndex + 7) % 7
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset)
}

/**
 * The date to show when the user switches to the Week view from another view. The Week view shows
 * the week containing the returned date.
 * - 1-day / 3-day: the date already shown (the selected day / first day of the window)
 * - month: today if it falls in the viewed month, otherwise the first of that month (so the first
 *   week shown may start in the previous month)
 */
export const getDateForWeekView = (
  currentDate: Date,
  fromView: '1-day' | '3-day' | 'week' | 'month',
  now: Date = new Date(),
): Date => {
  if (fromView !== 'month') return currentDate
  const inViewedMonth =
    now.getFullYear() === currentDate.getFullYear() && now.getMonth() === currentDate.getMonth()
  return inViewedMonth
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
    : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
}

/**
 * Get date range for a view
 * Returns date strings in YYYY-MM-DD format
 */
export const getDateRangeForView = (
  currentDate: Date,
  view: '1-day' | '3-day' | 'week' | 'month',
  weekStart?: string | null,
): { startDate: string; endDate: string } => {
  let startOfRange: Date
  let endOfRange: Date

  switch (view) {
    case '1-day': {
      startOfRange = currentDate
      endOfRange = currentDate
      break
    }
    case '3-day': {
      startOfRange = currentDate
      const threeDaysLater = new Date(currentDate)
      threeDaysLater.setDate(currentDate.getDate() + 2)
      endOfRange = threeDaysLater
      break
    }
    case 'week': {
      startOfRange = getWeekStartDate(currentDate, weekStart)
      endOfRange = new Date(
        startOfRange.getFullYear(),
        startOfRange.getMonth(),
        startOfRange.getDate() + 6,
      )
      break
    }
    case 'month': {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
      startOfRange = monthStart
      endOfRange = monthEnd
      break
    }
  }

  return {
    startDate: formatDateToYYYYMMDD(startOfRange),
    endDate: formatDateToYYYYMMDD(endOfRange),
  }
}

/**
 * Default start for a new event created from the "add" button, based on what the user is looking at.
 * - 1-day: the day being viewed
 * - 3-day: today if it is within the 3-day window, otherwise the first day of the window
 * - week: today if it is within the viewed week, otherwise the first day of that week
 * - month: today if it is within the viewed month, otherwise the first day of that month
 * The time of day is the next 15-minute increment from `now`, matching the event form's default.
 */
export const getDefaultNewEventStart = (
  viewDate: Date,
  view: '1-day' | '3-day' | 'week' | 'month',
  now: Date = new Date(),
  weekStart?: string | null,
): Date => {
  const viewStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), viewDate.getDate())
  let day: Date

  switch (view) {
    case '1-day':
      day = viewStart
      break
    case '3-day': {
      const windowEnd = new Date(viewStart)
      windowEnd.setDate(viewStart.getDate() + 2)
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      day = today >= viewStart && today <= windowEnd ? today : viewStart
      break
    }
    case 'week': {
      const weekFirst = getWeekStartDate(viewStart, weekStart)
      const weekLast = new Date(weekFirst)
      weekLast.setDate(weekFirst.getDate() + 6)
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      day = today >= weekFirst && today <= weekLast ? today : weekFirst
      break
    }
    case 'month':
      day =
        now.getFullYear() === viewDate.getFullYear() && now.getMonth() === viewDate.getMonth()
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
          : new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)
      break
  }

  const totalMinutes = Math.min(
    Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15,
    23 * 60 + 45,
  )
  const result = new Date(day)
  result.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0)
  return result
}
